import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormContainer } from 'react-hook-form-mui';
import { debounce } from 'lodash';
import { SearchIcon } from 'assets';
import { CompanyCard, Header, Input, Spinner } from 'view/components';
import { PublicHeader } from 'view/components/PublicHeader';
import { useAuth } from 'view/pages/auth/hooks/useAuth';
import { storageService } from 'infrastructure/services/StorageService';
import { ChatSocketProvider } from 'App';
import * as CS from 'view/pages/candidates/commonStyled';
import {
  COMPANIES_PER_PAGE,
  ES_SEARCH_MAX_RESULTS,
  useGetCompaniesList,
} from 'view/hooks/useGetCompaniesList';
import * as S from './styled';

export const CompaniesListPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Restore browse position from the URL so returning from a company page (Back) keeps the page.
  const initialSearch = searchParams.get('search') || '';
  const initialPage = Number(searchParams.get('page')) || 1;
  const [submittedQuery, setSubmittedQuery] = useState(initialSearch);
  const [page, setPage] = useState(initialPage);
  const [token, setToken] = useState<string | undefined>(undefined);

  const { isAuthenticated } = useAuth();
  const { getCompaniesList, data, isLoading } = useGetCompaniesList();

  // Keep the debounced search (created once) pointed at the latest getCompaniesList.
  const getListRef = useRef(getCompaniesList);
  getListRef.current = getCompaniesList;

  const debouncedSearch = useRef(
    debounce((query: string) => {
      setSubmittedQuery(query);
      getListRef.current(query, 1);
    }, 400)
  ).current;

  useEffect(() => {
    storageService.getItem('accessToken').then(setToken);
  }, []);

  // Initial browse — restore the page/search encoded in the URL (match-all when absent).
  useEffect(() => {
    getCompaniesList(initialSearch, initialPage);
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Mirror the current page + search into the URL so Back-navigation restores this exact view.
  useEffect(() => {
    const params: Record<string, string> = {};
    if (submittedQuery) {
      params.search = submittedQuery;
    }
    if (page > 1) {
      params.page = String(page);
    }
    setSearchParams(params, { replace: true });
  }, [page, submittedQuery, setSearchParams]);

  // Search-as-you-type: fire a debounced request on every keystroke, resetting to page 1.
  const onSearchChange = (value: string) => {
    setPage(1);
    debouncedSearch(value);
  };

  const handleClear = () => {
    debouncedSearch.cancel();
    setSubmittedQuery('');
    setPage(1);
    getCompaniesList('', 1);
  };

  const renderHeader = () => {
    if (isAuthenticated) {
      return (
        <ChatSocketProvider token={token}>
          <Header />
        </ChatSocketProvider>
      );
    }
    return <PublicHeader />;
  };

  const companies = data?.data || [];
  const total = data?.total || 0;

  // Cap browse depth at the ES ~10k-results limit (guide §8) — never offer "page 9999".
  const pageCount = Math.min(
    Math.ceil(total / COMPANIES_PER_PAGE),
    Math.floor(ES_SEARCH_MAX_RESULTS / COMPANIES_PER_PAGE)
  );

  const renderResults = () => {
    if (isLoading && companies.length === 0) {
      return (
        <S.SpinnerWrapper>
          <Spinner variant="outlined" />
        </S.SpinnerWrapper>
      );
    }

    if (companies.length === 0) {
      return <S.EmptyText>No companies found. Try a different search.</S.EmptyText>;
    }

    return (
      <>
        <S.Grid>
          {companies.map((company: any) => {
            return (
              <CompanyCard
                key={company.id}
                company={company}
                onClick={() => {
                  navigate(`/companies/${company.slug}`);
                }}
              />
            );
          })}
        </S.Grid>

        <CS.CustomPagination
          count={pageCount}
          shape="rounded"
          page={page}
          onChange={(e, value) => {
            setPage(value);
            getCompaniesList(submittedQuery, value);
          }}
          style={{ margin: 'auto auto 0' }}
        />
      </>
    );
  };

  return (
    <S.Wrapper>
      {renderHeader()}
      <S.MainContentWrapper>
        <S.PageTitle>Companies</S.PageTitle>

        <FormContainer defaultValues={{ query: initialSearch }}>
          <S.SearchContainer>
            <Input
              name="query"
              label=""
              placeholder="Search companies by name"
              marginbottom={0.1}
              startAdornment={<SearchIcon style={{ marginRight: '8px' }} />}
              onChange={(e: any) => {
                onSearchChange(e.target.value);
              }}
              clearable
              onClear={handleClear}
            />
          </S.SearchContainer>
        </FormContainer>

        <S.ResultsPanel>{renderResults()}</S.ResultsPanel>
      </S.MainContentWrapper>
    </S.Wrapper>
  );
};
